import Table from '@shared/components/Table';
import useFetchData from '@shared/hooks/useFetchData';
import { useState, useEffect } from 'react';
import { basisTerminationService } from '../services/basisTermination';
import { useTranslation } from 'react-i18next';
import { useStatusBadge } from '@shared/components/StatusBadge';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import BasisForTerminationForm from './BasisForTerminationForm';
import useModal from "@shared/hooks/useModal";
import { useForm } from 'react-hook-form';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from '@shared/components/DeleteModal';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import BasisForTerminationTranslation from './BasisForTerminationTranslation';

export default function BasisForTermination() {

    const { t } = useTranslation();
    const { page, fetchData, pageSize, sortBy, sortDir, data, totalPages, totalElements, setpage, setPageSize, searchQuery, setSearchQuery, handleSearchClick, setSortBy, setSortDir } = useFetchData();
    const [selectedActive, setSelectedActive] = useState('true');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const { statusBadge } = useStatusBadge();

    const formProps = useForm({
        defaultValues: {
            reasonKt: '',
            description: '',
            code: '',
            hrNssiReasonsId: '',
            sgebReasonLeavingCode: '',
            codeTerminationInsurance: '',
            active: true
        }
    });

    useEffect(() => {
        const params = {
            page,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
        };
        
        // Add active filter if selectedActive is set
        if (selectedActive !== null && selectedActive !== '') {
            params.active = selectedActive === 'true';
        }
        
        fetchData(basisTerminationService.getBasisTerminations, params);
    }, [page, pageSize, sortBy, sortDir, selectedActive]);

    const codeTerminationInsuranceLabels = {
        0: '',
        1: t('staff.basisForTermination.table.headers.codeTermination1'),
        2: t('staff.basisForTermination.table.headers.codeTermination2')
    };

    const columns = [
        {
            key: 'code',
            label: t('staff.basisForTermination.table.headers.code'),
            sortable: true,
        },
        {
            key: 'reasonKt',
            label: t('staff.basisForTermination.table.headers.reasonKt'),
            sortable: true,
        },
        {
            key: 'description',
            label: t('staff.basisForTermination.table.headers.description'),
            sortable: true,
        },
        {
            key: 'sgebReasonLeaving',
            label: t('staff.basisForTermination.table.headers.sgebReasonLeavingCode'),
            render: (item) => item.sgebReasonLeaving ? item.sgebReasonLeavingCode : '-',
            sortable: true,
        },
        {
            key: 'codeTerminationInsurance',
            label: t('staff.basisForTermination.table.headers.codeTerminationInsurance'),
            render: (item) => {
                const value = item.codeTerminationInsurance;
                return codeTerminationInsuranceLabels[value] !== undefined 
                    ? codeTerminationInsuranceLabels[value] 
                    : (value !== null && value !== undefined ? value : '-');
            },
            sortable: true,
        },
        {
            key: 'active',
            label: t('staff.basisForTermination.table.headers.active'),
            sortable: true,
        },
        {
            key: 'translated',
            label: t('staff.basisForTermination.table.headers.translated'),
            sortable: true,
        },
    ]

    // Report generator for basis termination
    const { 
        generatePdfReport, 
        generateExcelReport,
        pdfLoading,
        excelLoading
    } = useReportGenerator({
        title: t('staff.basisForTermination.title'),
        columns: [
            { key: 'code', label: t('staff.basisForTermination.table.headers.code') },
            { key: 'reasonKt', label: t('staff.basisForTermination.table.headers.reasonKt') },
            { key: 'description', label: t('staff.basisForTermination.table.headers.description') },
            { key: 'sgebReasonLeavingCode', label: t('staff.basisForTermination.table.headers.sgebReasonLeavingCode') },
            { 
                key: 'codeTerminationInsurance', 
                label: t('staff.basisForTermination.table.headers.codeTerminationInsurance'),
                render: (item) => {
                    const value = item.codeTerminationInsurance;
                    return codeTerminationInsuranceLabels[value] !== undefined 
                        ? codeTerminationInsuranceLabels[value] 
                        : (value !== null && value !== undefined ? value : '-');
                }
            },
            { 
                key: 'active', 
                label: t('staff.basisForTermination.table.headers.active'),
                render: (item) => item.active ? t('common.yes') : t('common.no')
            },
            { 
                key: 'translated', 
                label: t('staff.basisForTermination.table.headers.translated'),
                render: (item) => item.translated ? t('common.yes') : t('common.no')
            }
        ],
        fileName: 'basis_termination_report',
        companyName: '',
        reportService: () => {
            const params = { searchQuery };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            return basisTerminationService.getBasisTerminationsReportData(params);
        },
        customStyles: {}
    });

    const actionButtons = (
        <ActionButtons
            buttons={[{
                ...COMMON_BUTTONS.ADD,
                onClick: () => handleOpenModal('add')
            },
            {
                ...COMMON_BUTTONS.EDIT,
                onClick: () => handleOpenModal('edit')
                
            },
            {
                ...COMMON_BUTTONS.DELETE,
                onClick: () => handleOpenModal('delete')
            },
            {
                ...COMMON_BUTTONS.TRANSLATE,
                onClick: () => handleOpenModal('translate')
            },
            {
                ...COMMON_BUTTONS.DOWNLOAD_PDF,
                onClick: generatePdfReport,
                disabled: pdfLoading,
                icon: pdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                iconProps: pdfLoading ? { spin: true } : {},
                title: pdfLoading ? "Генериране на PDF..." : "common.downloadPDF",
                showWhen: 'always'
            },
            {
                ...COMMON_BUTTONS.EXCEL,
                onClick: generateExcelReport,
                disabled: excelLoading,
                icon: excelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                iconProps: excelLoading ? { spin: true } : {},
                title: excelLoading ? "Генериране на Excel..." : "common.downloadExcel",
                showWhen: 'always'
            }]}
        selectedRecord={selectedRecord}
        />
    )
    const searchOptions = [{ key: 'description', label: "description" }]

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleSort = (column) => {
        let newSortDir;
        if (sortBy === column) {
            newSortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            newSortDir = 'asc';
            setSortBy(column);
        }
        setSortDir(newSortDir);
        setpage(0);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit' && selectedRecord) {
                await basisTerminationService.updateBasisTermination(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await basisTerminationService.createBasisTermination(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            const params = {
                page,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
            };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            fetchData(basisTerminationService.getBasisTerminations, params);
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleConfirmDelete = useDeleteHandler({
        service: basisTerminationService,
        entityName: 'BasisTermination',
        selectedData: selectedRecord,
        fetchData,
        setSelectedData: setSelectedRecord,
        handleCloseModal,
        customFetchParams: {
            page,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            ...(selectedActive !== null && selectedActive !== '' && { active: selectedActive === 'true' }),
        },
        successMessage: t('common.deletedSuccessfully'),
        errorMessage: t('common.errorMessage'),
    });

    return (
        <>
            <Table
                title={t('staff.basisForTermination.title') || 'Basis for Termination'}
                columns={columns}
                actionButtons={actionButtons}
                data={data}
                selectedData={selectedRecord?.id}
                handleSearchClick={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    handleSearchClick(basisTerminationService.getBasisTerminations, params);
                }}
                getData={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    fetchData(basisTerminationService.getBasisTerminations, params);
                }}
                handleCheckboxChange={(index) => handleSelectRecord(data[index])}
                page={page}
                showPageSizeSelector={false}
                showPagination={true}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setpage={setpage}
                setPageSize={setPageSize}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                renderCell={(item, column) => {
                    // If column has a custom render function, use it
                    if (column.render) {
                        return column.render(item);
                    }
                    // Otherwise, use statusBadge for active/translated columns
                    return statusBadge(item, column);
                }}
                sortBy={sortBy}
                sortDir={sortDir}
                handleSort={handleSort}
            />

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <BasisForTerminationForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.description || selectedRecord?.reasonKt}
                    entityType="record"
                />
            )}

            {modalState.visible && modalState.type === 'translate' && (
                <BasisForTerminationTranslation
                    onClose={handleCloseModal}
                    record={selectedRecord}
                    onRefresh={() => {
                        const params = {
                            page,
                            pageSize,
                            sortBy,
                            sortDir,
                            searchQuery,
                        };
                        if (selectedActive !== null && selectedActive !== '') {
                            params.active = selectedActive === 'true';
                        }
                        return fetchData(basisTerminationService.getBasisTerminations, params);
                    }}
                />
            )}
        </>
    )
}

